/**
 * The lab desk's view of Document Intake (PROD-3204, PM slice 4, boards N1-N3 and N7): which requests a report can
 * cover, which requests to ask about, and what a request's latest lab-desk run says on its row.
 */
import type { PatientLabOrderDetail } from '@/types/scheduling-api';
import { LAB_ORDER_STATUS } from '@/types/scheduling-api';
import { LAB_REQUEST_INTAKE_MAX } from '../api/intake-client';
import type { LabRequestIntake } from '../types';
import { STEP_STAGE, type Chip, type Glyph } from './inbox-view';

/** A desk row with what the upload needs. Split parts (PROD-3115) never take a report here. */
type DeskRow = PatientLabOrderDetail & { splitPart?: unknown };

const hasIds = (r: DeskRow): r is DeskRow & { id: string; patientId: string } => !!r.id && !!r.patientId && !r.splitPart;

/** Rows whose run can matter: waiting for a report, or with a report that may have come from a lab-desk upload. */
const ASKED = new Set<string>([LAB_ORDER_STATUS.SampleCollected, LAB_ORDER_STATUS.ResultReceived, LAB_ORDER_STATUS.ResultReviewed]);

export interface DeskIntakeAsk {
  patientId: string;
  labRequestIds: string[];
}

/** One read per patient on the page (at most 50 ids each), for the rows that can have a lab-desk run. */
export function deskIntakeAsks(rows: DeskRow[]): DeskIntakeAsk[] {
  const byPatient = new Map<string, string[]>();
  for (const r of rows) {
    if (!hasIds(r) || !ASKED.has(r.orderStatus ?? '')) continue;
    const ids = byPatient.get(r.patientId) ?? [];
    if (!ids.includes(r.id)) ids.push(r.id);
    byPatient.set(r.patientId, ids);
  }
  const asks: DeskIntakeAsk[] = [];
  for (const [patientId, ids] of byPatient) {
    for (let i = 0; i < ids.length; i += LAB_REQUEST_INTAKE_MAX) {
      asks.push({ patientId, labRequestIds: ids.slice(i, i + LAB_REQUEST_INTAKE_MAX) });
    }
  }
  return asks;
}

/**
 * The requests one report can cover (N2): this patient's Sample collected requests of the same encounter. The
 * requests the upload started from are always listed (an upload of a clearer copy starts from a Result received row).
 */
export function coverableRequests(rows: DeskRow[], from: DeskRow[]): (DeskRow & { id: string; patientId: string })[] {
  const first = from.find(hasIds);
  if (!first) return [];
  const fromIds = new Set(from.filter(hasIds).map((r) => r.id));
  const seen = new Set<string>();
  return rows.filter(hasIds).filter((r) => {
    if (seen.has(r.id)) return false;
    const listed =
      fromIds.has(r.id) ||
      (r.patientId === first.patientId &&
        (r.encounterId ?? null) === (first.encounterId ?? null) &&
        r.orderStatus === LAB_ORDER_STATUS.SampleCollected);
    if (listed) seen.add(r.id);
    return listed;
  });
}

export const isDeskInFlight = (i: LabRequestIntake | null | undefined) => i?.status === 'Queued' || i?.status === 'Running';

export type DeskIntakeAction = 'review' | 'not-read';

export interface DeskIntakeView {
  /** The line under the request status: a glyph while a file is read or once it is filed, a chip when it waits. */
  line: { glyph: Glyph } | { chip: Chip } | null;
  action: DeskIntakeAction | null;
  /** A report for this request is being read or waits for review: no second upload. */
  busy: boolean;
}

const NONE: DeskIntakeView = { line: null, action: null, busy: false };

/** What the request's latest lab-desk run says on its row (N3). */
export function deskIntakeView(i: LabRequestIntake | null | undefined): DeskIntakeView {
  if (!i) return NONE;
  switch (i.status) {
    case 'Queued':
      return { line: { glyph: { label: 'Reading', kind: 'half' } }, action: null, busy: true };
    case 'Running': {
      const at = i.step ? (STEP_STAGE[i.step] ?? 0) : 0;
      return { line: { glyph: { label: at > 0 ? `Reading · step ${at} of 5` : 'Reading', kind: 'half' } }, action: null, busy: true };
    }
    case 'AwaitingReview':
      return { line: { chip: { label: 'Ready for review', semantic: 'info' } }, action: 'review', busy: true };
    case 'Failed':
      return { line: { chip: { label: 'Could not be read', semantic: 'error' } }, action: 'not-read', busy: false };
    case 'Completed':
      // Patient #1153: a report is linked when the upload named exactly one request; otherwise it is an outside report.
      return {
        line: { glyph: i.linkedLabRequestId ? { label: 'Linked to order', kind: 'done' } : { label: 'Saved as outside report', kind: 'done' } },
        action: null,
        busy: false,
      };
    default:
      return NONE;
  }
}

/** Failures of the reading service itself: reading the same file again can work. */
const SERVICE_FAILURES = new Set(['publish_failed', 'lab_catalogue_unavailable', 'observation_lookup_unavailable']);

export const isServiceFailure = (code: string | null | undefined) =>
  !!code && (SERVICE_FAILURES.has(code) || code.endsWith('_unavailable') || code.endsWith('_timeout'));

/**
 * N7: why the report was not read, and what to do. The server attaches the report and moves the request to Result
 * received (G4), by a sweep that can take about 2 minutes: until `patientDocumentId` is set, the text says it is coming.
 */
export function notReadText(code: string | null | undefined, filed: boolean): string {
  const attached = filed
    ? 'The report is attached to the request, and the request is now Result received.'
    : 'The report is being attached to the request. The request moves to Result received within a few minutes.';
  return isServiceFailure(code)
    ? `The reading service did not answer. ${attached} Read it again, or enter the values by hand.`
    : `Scrips could not read values from this file. ${attached} Enter the values by hand, or upload a clearer copy.`;
}

/** "412 KB", "2.4 MB". */
export function fileSizeText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
